/**
 * useSeasonStore — Zustand store (persisted) for the seasonal battle-pass.
 *
 * State:
 *   seasonXp        — accumulated XP for the current season
 *   premiumUnlocked — whether the premium track is purchased
 *   claimedLevels   — array of level numbers for which rewards have been claimed
 *                     (each level can be claimed twice: once for free, once for premium)
 *                     Format: { level: number; track: 'free' | 'premium' }[]
 *
 * Season Pass (server-synced) fields:
 *   seasonSp        — season points from server
 *   seasonTier      — unlocked tier (0-10)
 *   seasonPremiumOwned — premium track purchased
 *   seasonClaimedFree  — claimed free tier indices
 *   seasonClaimedPrem  — claimed premium tier indices
 *   freeRewards     — reward definitions for free track
 *   premiumRewards  — reward definitions for premium track
 *
 * Actions:
 *   addSeasonXp(amount)         — increase seasonXp
 *   claimReward(level, track)   — mark a reward as claimed
 *   unlockPremium()             — set premiumUnlocked = true (called after IAP success)
 *   reset()                     — resets store for a new season (preserves key structure)
 *   loadSeasonPass(token)       — fetch season pass status from server
 *   claimFreeReward(tierIndex, token)    — POST claim-free to server
 *   claimPremiumReward(tierIndex, token) — POST claim-premium to server
 *   buyPremium(token)           — POST buy-premium to server (deducts 200 gems client-side)
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { SeasonService } from '../services/SeasonService.js'
import { useProgressionStore } from './useProgressionStore.js'

export type RewardTrack = 'free' | 'premium'

export interface ClaimedEntry {
  level: number
  track: RewardTrack
}

export interface PassReward {
  type: string
  amount?: number
  packType?: string
  title?: string
}

const API_BASE = typeof window !== 'undefined' && window.location.origin.includes('localhost')
  ? 'http://localhost:3001'
  : ''

async function apiFetch(path: string, token: string, opts: RequestInit = {}): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts.headers ?? {}),
    },
  })
}

interface SeasonState {
  // Legacy XP-based progression (kept for backward compat)
  seasonXp: number
  premiumUnlocked: boolean
  claimedLevels: ClaimedEntry[]
  seasonEndRewardClaimed: boolean
  previousSeasonLeague: string | null
  previousSeasonTitle: string | null

  // Server-synced season pass state
  seasonSp: number
  seasonTier: number
  seasonPremiumOwned: boolean
  seasonClaimedFree: number[]
  seasonClaimedPrem: number[]
  freeRewards: PassReward[]
  premiumRewards: PassReward[]
  seasonPassLoading: boolean
  seasonPassError: string | null

  addSeasonXp: (amount: number) => void
  claimReward: (level: number, track: RewardTrack) => void
  unlockPremium: () => void
  reset: () => void
  isRewardClaimed: (level: number, track: RewardTrack) => boolean
  claimSeasonEndReward: (league: string) => void
  resetForNewSeason: () => void

  // Server-synced actions
  loadSeasonPass: (token: string) => Promise<void>
  claimFreeReward: (tierIndex: number, token: string) => Promise<PassReward | null>
  claimPremiumReward: (tierIndex: number, token: string) => Promise<PassReward | null>
  buyPremium: (token: string) => Promise<boolean>
  earnSP: (amount: number, token: string) => Promise<void>
}

export const useSeasonStore = create<SeasonState>()(
  persist(
    (set, get) => ({
      // Legacy fields
      seasonXp: 0,
      premiumUnlocked: false,
      claimedLevels: [],
      seasonEndRewardClaimed: false,
      previousSeasonLeague: null,
      previousSeasonTitle: null,

      // Season pass server-synced fields
      seasonSp: 0,
      seasonTier: 0,
      seasonPremiumOwned: false,
      seasonClaimedFree: [],
      seasonClaimedPrem: [],
      freeRewards: [],
      premiumRewards: [],
      seasonPassLoading: false,
      seasonPassError: null,

      addSeasonXp: (amount) =>
        set((s) => ({ seasonXp: Math.max(0, s.seasonXp + amount) })),

      claimReward: (level, track) => {
        const { claimedLevels } = get()
        const alreadyClaimed = claimedLevels.some(
          (e) => e.level === level && e.track === track,
        )
        if (alreadyClaimed) return
        set((s) => ({
          claimedLevels: [...s.claimedLevels, { level, track }],
        }))
      },

      unlockPremium: () => set({ premiumUnlocked: true }),

      reset: () =>
        set({ seasonXp: 0, premiumUnlocked: false, claimedLevels: [] }),

      isRewardClaimed: (level, track) =>
        get().claimedLevels.some((e) => e.level === level && e.track === track),

      claimSeasonEndReward: (league: string) => {
        const { seasonEndRewardClaimed } = get()
        if (seasonEndRewardClaimed) return
        const rewards = SeasonService.getInstance().getSeasonEndRewards(league)
        useProgressionStore.getState().addGold(rewards.gold)
        useProgressionStore.getState().addGems(rewards.gems)
        set({
          seasonEndRewardClaimed: true,
          previousSeasonLeague: league,
          previousSeasonTitle: rewards.title,
        })
      },

      resetForNewSeason: () =>
        set((s) => ({
          seasonXp: 0,
          premiumUnlocked: false,
          claimedLevels: [],
          seasonEndRewardClaimed: false,
          previousSeasonLeague: s.previousSeasonLeague,
          previousSeasonTitle: s.previousSeasonTitle,
        })),

      // ── Server-synced season pass actions ────────────────────────────────

      loadSeasonPass: async (token: string) => {
        set({ seasonPassLoading: true, seasonPassError: null })
        try {
          const res = await apiFetch('/api/season/pass', token)
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          const data = await res.json() as {
            sp: number
            tier: number
            premiumOwned: boolean
            claimedFree: number[]
            claimedPrem: number[]
            freeRewards: PassReward[]
            premiumRewards: PassReward[]
          }
          set({
            seasonSp: data.sp,
            seasonTier: data.tier,
            seasonPremiumOwned: data.premiumOwned,
            seasonClaimedFree: data.claimedFree,
            seasonClaimedPrem: data.claimedPrem,
            freeRewards: data.freeRewards,
            premiumRewards: data.premiumRewards,
            seasonPassLoading: false,
          })
        } catch (err) {
          set({ seasonPassLoading: false, seasonPassError: String(err) })
        }
      },

      claimFreeReward: async (tierIndex: number, token: string): Promise<PassReward | null> => {
        try {
          const res = await apiFetch('/api/season/pass/claim-free', token, {
            method: 'POST',
            body: JSON.stringify({ tierIndex }),
          })
          if (!res.ok) return null
          const data = await res.json() as { ok: boolean; reward: PassReward }
          if (data.ok) {
            set((s) => ({
              seasonClaimedFree: [...s.seasonClaimedFree, tierIndex],
            }))
            // Apply reward to local progression
            const reward = data.reward
            const prog = useProgressionStore.getState()
            if (reward.type === 'gold' && reward.amount)  prog.addGold(reward.amount)
            if (reward.type === 'xp'   && reward.amount)  prog.addXp(reward.amount)
            if (reward.type === 'gems' && reward.amount)  prog.addGems(reward.amount)
            return reward
          }
          return null
        } catch {
          return null
        }
      },

      claimPremiumReward: async (tierIndex: number, token: string): Promise<PassReward | null> => {
        try {
          const res = await apiFetch('/api/season/pass/claim-premium', token, {
            method: 'POST',
            body: JSON.stringify({ tierIndex }),
          })
          if (!res.ok) return null
          const data = await res.json() as { ok: boolean; reward: PassReward }
          if (data.ok) {
            set((s) => ({
              seasonClaimedPrem: [...s.seasonClaimedPrem, tierIndex],
            }))
            const reward = data.reward
            const prog = useProgressionStore.getState()
            if (reward.type === 'gold' && reward.amount)  prog.addGold(reward.amount)
            if (reward.type === 'xp'   && reward.amount)  prog.addXp(reward.amount)
            if (reward.type === 'gems' && reward.amount)  prog.addGems(reward.amount)
            return reward
          }
          return null
        } catch {
          return null
        }
      },

      buyPremium: async (token: string): Promise<boolean> => {
        const prog = useProgressionStore.getState()
        if (prog.gems < 200) return false
        // Deduct gems optimistically
        prog.addGems(-200)
        try {
          const res = await apiFetch('/api/season/pass/buy-premium', token, {
            method: 'POST',
          })
          if (!res.ok) {
            // Refund gems on failure
            prog.addGems(200)
            return false
          }
          set({ seasonPremiumOwned: true, premiumUnlocked: true })
          return true
        } catch {
          prog.addGems(200)
          return false
        }
      },

      earnSP: async (amount: number, token: string): Promise<void> => {
        try {
          const res = await apiFetch('/api/season/pass/earn', token, {
            method: 'POST',
            body: JSON.stringify({ amount }),
          })
          if (!res.ok) return
          const data = await res.json() as { ok: boolean; sp: number; tier: number }
          if (data.ok) {
            set({ seasonSp: data.sp, seasonTier: data.tier })
          }
        } catch {
          // silent — SP earn is best-effort
        }
      },
    }),
    {
      name: 'ultimaze-season',
      onRehydrateStorage: () => (state) => {
        if (state !== undefined && state.claimedLevels === undefined) {
          state.claimedLevels = []
        }
      },
    },
  ),
)
